/**
 * All Jobs Search API Service
 * Search for ALL types of jobs (Full-Time, Part-Time, Contract, Freelance, Internship)
 * Integrates with JSearch API + Google Jobs API
 */

import { searchJSearch, isAPIConfigured as isJSearchConfigured } from './jobSearchAPI';
import { searchGoogleJobs, isGoogleJobsAPIConfigured } from './googleJobsAPI';

/**
 * Search for ALL types of jobs (not limited to freelance)
 * 
 * @param {Object} params - Search parameters
 * @param {string} params.query - Search query (e.g., "software developer")
 * @param {string} params.location - Location filter (e.g., "United Kingdom", "London")
 * @param {string[]} params.jobTypes - Job types to search (default: all types)
 *   Options: ['FULLTIME', 'PARTTIME', 'CONTRACTOR', 'INTERN']
 * @param {boolean} params.remote - Remote jobs only (default: false)
 * @param {number} params.page - Page number (default: 1)
 * @param {string} params.datePosted - Date filter (default: 'all')
 *   Options: 'all', 'today', '3days', 'week', 'month'
 * @returns {Promise<Object>} Combined job search results from all APIs
 */
export async function searchAllJobs({
  query = 'developer',
  location = 'United Kingdom',
  jobTypes = ['FULLTIME', 'PARTTIME', 'CONTRACTOR', 'INTERN'], // All types by default
  remote = false,
  page = 1,
  datePosted = 'all',
}) {
  console.log('🔍 Searching ALL job types...');
  console.log(`   Query: ${query}`);
  console.log(`   Location: ${location}`);
  console.log(`   Job Types: ${jobTypes.join(', ')}`);
  console.log(`   Remote: ${remote}`);

  const results = {
    success: false,
    jobs: [],
    total: 0,
    sources: {},
    errors: [],
  };

  // Check which APIs are configured
  const jsearchConfigured = isJSearchConfigured();
  const googleConfigured = isGoogleJobsAPIConfigured();

  console.log(`📡 APIs Available:`);
  console.log(`   JSearch: ${jsearchConfigured ? '✅' : '❌'}`);
  console.log(`   Google Jobs: ${googleConfigured ? '✅' : '❌'}`);

  // Search JSearch API
  if (jsearchConfigured) {
    try {
      const jsearchResult = await searchJSearch({
        query,
        location,
        employment_types: jobTypes.join(','), // e.g., "FULLTIME,PARTTIME,CONTRACTOR,INTERN"
        remote_jobs_only: remote,
        page,
        num_pages: 1,
        date_posted: datePosted,
      });

      if (jsearchResult.success && jsearchResult.jobs) {
        const jsearchJobs = jsearchResult.jobs.map(job => ({
          ...job,
          dataSource: 'JSearch',
        }));
        
        results.jobs.push(...jsearchJobs);
        results.sources.jsearch = {
          count: jsearchJobs.length,
          status: 'success',
        };
        
        console.log(`   jsearch: ✅ ${jsearchJobs.length} jobs`);
      }
    } catch (error) {
      console.error('JSearch API error:', error);
      results.errors.push({ source: 'jsearch', error: error.message });
      results.sources.jsearch = {
        count: 0,
        status: 'error',
        error: error.message,
      };
    }
  }

  // Search Google Jobs API
  if (googleConfigured) {
    try {
      const googleResult = await searchGoogleJobs({
        query,
        location,
        remote_jobs_only: remote,
      });

      if (googleResult.success && googleResult.jobs) {
        // Filter Google Jobs by job type if needed
        let googleJobs = googleResult.jobs.map(job => ({
          ...job,
          dataSource: 'Google Jobs',
        }));

        // If specific job types requested, filter Google results
        if (jobTypes.length < 4) { // Not all types
          googleJobs = googleJobs.filter(job => {
            const jobType = (job.type || job.jobType || '').toUpperCase();
            return jobTypes.some(type => jobType.includes(type));
          });
        }
        
        results.jobs.push(...googleJobs);
        results.sources.googleJobs = {
          count: googleJobs.length,
          status: 'success',
        };
        
        console.log(`   googleJobs: ✅ ${googleJobs.length} jobs`);
      }
    } catch (error) {
      console.error('Google Jobs API error:', error);
      results.errors.push({ source: 'googleJobs', error: error.message });
      results.sources.googleJobs = {
        count: 0,
        status: 'error',
        error: error.message,
      };
    }
  }

  // Remove duplicates based on job title + company
  const uniqueJobs = removeDuplicates(results.jobs);
  
  results.jobs = uniqueJobs;
  results.total = uniqueJobs.length;
  results.success = uniqueJobs.length > 0;

  console.log(`✅ Total unique jobs found: ${results.total}`);
  
  return results;
}

/**
 * Search for specific job type
 */
export async function searchByJobType(jobType, params = {}) {
  const jobTypeMap = {
    'fulltime': ['FULLTIME'],
    'parttime': ['PARTTIME'],
    'contract': ['CONTRACTOR'],
    'freelance': ['CONTRACTOR'],
    'internship': ['INTERN'],
    'all': ['FULLTIME', 'PARTTIME', 'CONTRACTOR', 'INTERN'],
  };

  const types = jobTypeMap[jobType.toLowerCase()] || jobTypeMap['all'];

  return searchAllJobs({
    ...params,
    jobTypes: types,
  });
}

/**
 * Remove duplicate jobs
 */
function removeDuplicates(jobs) {
  const seen = new Set();
  const unique = [];

  for (const job of jobs) {
    const key = `${(job.title || '').toLowerCase()}_${(job.company || '').toLowerCase()}`;
    
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(job);
    }
  }

  return unique;
}

/**
 * Get API status
 */
export function getAPIStatus() {
  return {
    jsearch: isJSearchConfigured(),
    googleJobs: isGoogleJobsAPIConfigured(),
    total: (isJSearchConfigured() ? 1 : 0) + (isGoogleJobsAPIConfigured() ? 1 : 0),
  };
}

/**
 * Get configured APIs
 */
export function getConfiguredAPIs() {
  const apis = [];
  
  if (isJSearchConfigured()) apis.push('JSearch');
  if (isGoogleJobsAPIConfigured()) apis.push('Google Jobs');
  
  return {
    apis,
    total: apis.length,
    jsearch: isJSearchConfigured(),
    googleJobs: isGoogleJobsAPIConfigured(),
  };
}

export default {
  searchAllJobs,
  searchByJobType,
  getAPIStatus,
  getConfiguredAPIs,
};

